<template>
  <section
    :class="[
      'wrapper',
      'content',
      'content--centered',
      $arMediaQuery.pageContent.maxWidth('sm') && 'sm-max',
      $arMediaQuery.pageContent.maxWidth('xs') && 'xs-max',
    ]">
    <am2-step-link
      class="step-back-link"
      text="Back to integrations"
      @click="handleBackLinkClick" />
    <div
      :class="[
        'title-section',
        $arMediaQuery.pageContent.maxWidth('md') && 'md-max',
      ]">
      <div class="title-section-left">
        <am2-icon-title-section
          title="Universe"
          description="Automatically sync your ticket sales, events & attendees from Universe"
          :icon-props="{
            name: 'universe',
            color: $arStyle.color.universe,
            height: '46px',
            width: '40px',
          }"
        />
      </div>
      <div class="title-section-right" v-if="!isFetchingUniverseIntegration && integrationsSummary">
        <am2-integration-account-select
          v-if="!isFetchingUniverseIntegration && universeIntegration && currentIntegration"
          :default-selected-integration-oid="currentIntegration.oid"
          :available-integrations="integrationsSummary"
          @select="handleIntegrationChange"
          @new-account="handleAddNewIntegration"
          :style="{ marginRight: '10px', maxWidth: 'calc(100vw - 80px)', }"
        />
        <am2-expand-button-dropdown
          align="left"
          :button-props="{ iconName: 'settings' }"
          :items="[
            {
              name: 'Open Universe',
              value: 'goToUniverseSite',
            },
            {
              name: 'Reconnect Integration',
              value: 'reconnectIntegration',
            },
            {
              name: 'Remove Account',
              value: 'removeIntegration',
            },
          ]"
          @select="handleSettingOptionSelect" />
      </div>
    </div>
    <am2-tabs
      :items="[
        { title: 'Notifications' },
        { title: 'Status' }
      ]"
      :selected-tab-index="selectedTabIndex"
      class="tab-options"
      @select="(selected, idx) => selectedTabIndex = idx"
    />
    <div
      v-if="selectedTabIndex === 0"
    >
      <NotificationsContainer
        :integration="currentIntegration"
        :loading="isFetchingUniverseIntegration"
      />
    </div>
    <div
      v-if="selectedTabIndex === 1"
      class="integration-status-container"
    >
      <section v-if="isAddingUniverseIntegration || isFetchingUniverseIntegration" class="integration-loading-container">
        <am2-loading-bubble />
      </section>
      <section v-else>
        <section v-if="integrationFailed" class="integration-failed-message">
          <ar-snackbar
            type="warning"
            message="Your Universe connection is no longer valid. This means we can't sync your member data. <a>Please reconnect your account</a>"
            @anchorClick="handleReconnectIntegration"
            :style="{ width: '100%' }"
          />
        </section>

        <section v-else>
          <am2-card-container
            class="sync-block-container"
          >
            <div v-if="latestTaskStatus === 'in-progress'" class="sync-block">
              <am2-loading-bubble
                class="sync-animation"
              />
              <div class="text-block">
                <am2-heading
                  class="header"
                  size="md"
                  type="h2"
                  :title="syncingTaskStatusCopy"/>
                <div class="button-block">
                  <ar-simple-button
                    text="Stop sync"
                    outlined
                    class="button sync-btn"
                    @click="handleStopSync" />
                </div>
              </div>
            </div>
            <div v-else class="sync-block">
              <template>
                <div
                  v-if="latestTaskStatus === 'completed'"
                  class="tick-container"
                  :style="{
                    backgroundColor: $arStyle.color.green500,
                  }"
                >
                  <ar-icon
                    name="check"
                    class="tick-icon"
                    width="18"
                    height="17"
                  />
                </div>
                <div
                  v-else
                  class="tick-container"
                  :style="{
                    backgroundColor: $arStyle.color.purple500,
                  }"
                >
                  <ar-icon
                    name="exclamation-mark"
                    class="tick-icon"
                    height="25"
                  />
                </div>
              </template>
              <div class="text-block u-margin-top-8">
                <am2-heading
                  class="header"
                  size="md"
                  type="h2"
                  :title="syncingTaskStatusCopy"/>
                <div class="button-block u-margin-top-9">
                  <ar-simple-button
                    :text="syncButtonCopy"
                    class="button sync-btn"
                    :loading="isFetchingTasks"
                    @click="handleStartSync" />
                </div>
                <div v-if="hasSyncedBefore" class="last-sync-time">
                  <ar-text
                    :text="`Last synced ${timeSinceLastSync}`"
                    size="xxs"
                    :style="{
                      color: '#8E97A7',
                    }"
                  />
                </div>
              </div>
            </div>
          </am2-card-container>
        </section>
      </section>
    </div>

  </section>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex';
import moment from 'moment';
import NotificationsContainer from '../components/notifications-container';

export default {
  name: 'UniverseIntegration',
  layout: 'default',
  components: {
    NotificationsContainer
  },

  data: () => ({
    currentlySelectedIntegrationIndex: 0,
    syncStatusPoller: null,
    selectedTabIndex: 1
  }),

  computed: {
    ...mapState({
      universeIntegration: state => state.universeIntegration.integration,
      isFetchingUniverseIntegration: state => state.universeIntegration.isFetchingIntegration,
      hasFetchedUniverseIntegration: state => state.universeIntegration.hasFetchedIntegration,
      isAddingUniverseIntegration: state => state.universeIntegration.isAddingIntegration,
      isDeletingUniverseIntegration: state => state.universeIntegration.isDeletingIntegration,
      isReconnectingUniverseIntegration: state => state.universeIntegration.isReconnectingIntegration,
      isFetchingTasks: state => state.universeIntegration.isFetchingTasks,
      tasks: state => state.universeIntegration.tasks,
      latestTaskStatus: state => state.universeIntegration.latestTaskStatus,
    }),

    universeIntegrationEnabled() {
      return process.env.arEnableUniverseIntegration;
    },

    latestTask() {
      if (!this.tasks || this.tasks.length < 1) return null;
      return this.tasks[0];
    },

    syncButtonCopy() {
      if (this.latestTaskStatus === 'in-progress') {
        return 'Sync now';
      } else {
        return 'Sync again';
      }
    },

    syncingTaskStatusCopy() {
      switch (this.latestTaskStatus) {
        case 'in-progress':
          return 'Syncing events from Universe';
        case 'pending':
          return 'The events syncing is still pending';
        case 'completed':
          return 'Your Universe account is currently connected';
        case 'cancelled':
          return 'Your Universe account connection is cancelled';
        case 'failed':
          return this.latestTask.statusDetails.lastError;
        default:
          return 'Your Universe account is currently connected';
      }
    },

    integrationsSummary() {
      if (!this.universeIntegration) return null;
      return this.universeIntegration
        .filter(i => i.status !== 'failed')
        .map(i => {
          const name = `${i.integration?.user?.firstName || ''} ${i.integration?.user?.lastName || ''}`;
          return {
            oid: i.oid,
            name: name,
            email: name,
          };
        });
    },

    currentIntegration() {
      if (this.currentlySelectedIntegrationIndex === null) return null;
      if (!this.universeIntegration || this.universeIntegration.length < 1 || !this.universeIntegration[this.currentlySelectedIntegrationIndex]) return null;
      return this.universeIntegration[this.currentlySelectedIntegrationIndex];
    },

    integrationFailed() {
      if (!this.currentIntegration) return false;
      return this.currentIntegration.status === 'failed';
    },

    blockTitle() {
      return this.isSyncing ? `Syncing X of Y members from Universe` : 'Your Universe account is currently connected';
    },

    isSyncing() {
      if (!this.currentIntegration || !this.hasExistingTask) {
        return false;
      }
      return this.latestTaskStatus === 'in-progress';
    },

    hasSyncedBefore() {
      return (
        this.latestTask && this.latestTask.status === 'completed'
      );
    },

    timeSinceLastSync() {
      let sinceNow = "";

      if (this.hasSyncedBefore) {
        sinceNow = moment.utc(
          this.latestTask.sysMtime
        ).fromNow();
      }

      return sinceNow;
    }
  },

  async mounted() {
    if (!this.universeIntegrationEnabled) {
      this.handleBackLinkClick();
    }
    this.fetchInitialData();
  },

  beforeDestroy() {
    this['universeIntegration/RESET_INTEGRATION']();

    if (this.syncStatusPoller) {
      clearInterval(this.syncStatusPoller);
    }
  },

  methods: {
    ...mapActions([
      'SHOW_CONFIRM',
      'universeIntegration/FETCH_INTEGRATION',
      'universeIntegration/ADD_INTEGRATION',
      'universeIntegration/DELETE_INTEGRATION',
      'universeIntegration/RECONNECT_INTEGRATION',
      'universeIntegration/SYNC_START',
      'universeIntegration/SYNC_STOP',
      'universeIntegration/FETCH_TASKS',
      'CONNECT_TO_INTEGRATION',
      'promoterTasks/START_POLLING_PENDING_TASKS',
    ]),
    ...mapMutations([
      'universeIntegration/RESET_INTEGRATION',
    ]),
    async fetchInitialData() {
      if (this.isFetchingUniverseIntegration || this.hasFetchedIntegration) return;
      await this['universeIntegration/FETCH_INTEGRATION']();

      if (this.currentIntegration?.oid && !this.isFetchingTasks) {
        this['universeIntegration/FETCH_TASKS']({
          oid: this.currentIntegration.oid,
          orderby: "sysMtime desc",
          filter: {
            name: "event-data-synch",
            promoterIntegrationOid: this.currentIntegration.oid,
          }
        });
      }
    },

    async updateData() {
      await this['universeIntegration/FETCH_TASKS']({
        orderby: "sysMtime desc",
        top: 1,
        oid: this.currentIntegration.oid,
      });

      if (this.currentIntegration && this.hasExistingTask && this.latestTaskStatus !== "in-progress") {
        window.clearInterval(this.syncStatusPoller);
        this.syncStatusPoller = null;
        this['promoterTasks/START_POLLING_PENDING_TASKS']({});
      }
    },

    hasExistingTask() {
      return this.latestTask !== null;
    },

    handleBackLinkClick() {
      this.$router.push({ path: '/settings/integrations' });
    },

    handleIntegrationChange(integration, index) {
      this.currentlySelectedIntegrationIndex = index;
    },

    async handleAddNewIntegration() {
      this.isConnecting = true;

      try {
        const res = await this.CONNECT_TO_INTEGRATION({
          provider: 'universe',
          app: 'universe'
        });
        this.$router.push({
          path: `/settings/integrations/universe`,
        });
        this.isConnecting = false;
        this.fetchIntegration('universe', 'universe'); // TODO - Once we integrate the connect methods into the modules, we wont need to do this
      } catch (e) {
        // If it's closed by user, then it's all good
        if (e.name === 'USER_CLOSE') {
          return;
        }

        this.$arNotification.push({ type: 'error', message: `Failed to connect to Universe: ${e.message}` });
        throw e;
      } finally {
        this.isConnecting = false;
      }
    },

    handleReconnectIntegration() {
      this['universeIntegration/RECONNECT_INTEGRATION']({oid: this.currentIntegration.oid});
    },

    async handleStartSync() {
      setTimeout(() => {
        this['promoterTasks/START_POLLING_PENDING_TASKS']({});
      }, 750);
      this['universeIntegration/SYNC_START']({oid: this.currentIntegration.oid});
      this.syncStatusPoller = setInterval(this.updateData, 3000);
    },

    handleStopSync() {
      this['universeIntegration/SYNC_STOP']({oid: this.currentIntegration.oid});
      window.clearInterval(this.syncStatusPoller);
      this.syncStatusPoller = null;
      setTimeout(() => {
        this['promoterTasks/START_POLLING_PENDING_TASKS']({});
      }, 750);
    },

    async handleSettingOptionSelect(item) {
      if (item.value === 'removeIntegration') {
        const answer = await this.SHOW_CONFIRM({
          messageHtml: 'Removing the Universe integration will stop syncing your orders and customer data from Universe.',
          confirmButtonText: 'Remove integration',
          iconName: 'alert-question-mark',
          title: 'Are you sure?',
        });
        if (answer) {
          try {
            await this['universeIntegration/DELETE_INTEGRATION']({oid: this.currentIntegration.oid});
            if (this.universeIntegration.length > 1) {
              this.currentlySelectedIntegrationIndex = 0;
            } else {
              this.$router.push({ path: '/settings/integrations' });
            }
          } catch (e) {
            this.$arNotification.push({ type: 'error', message: 'Failed to remove integration' });
          }
        }
      } else if (item.value === 'reconnectIntegration') {
        this.handleReconnectIntegration();
      } else if (item.value === 'goToUniverseSite') {
        if (!this.currentIntegration) return;
        var win = window.open('https://www.universe.com/', '_blank');
        win.focus();
      }
    },
  }
};
</script>

<style lang="scss" scoped>
.wrapper {

  .tab-options {
    margin: 60px 0 20px 0;
    border-bottom: 1px $skyBlueGrey500 solid;
  }

  &.sm-max {
    padding: 0 24px;
  }
  &.xs-max {
    padding: 0 12px;
  }

  .integration-loading-container {
    display: flex;
    justify-content: center;
    margin-top: 65px;
  }

  .step-back-link {
    margin-top: 48px;
  }

  .title-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 48px;

    .title-section-left {
      display: inline-flex;
      align-items: flex-start;

      .tag {
        position: relative;
        margin-left: 10px;
        top: 4px;
      }
    }

    .title-section-right {
      display: inline-flex;
      align-items: center;

      .select {
        width: 300px;
        margin-right: 10px;
      }
    }

    &.md-max {
      flex-direction: column;
      align-items: flex-start;

      .title-section-right {
        margin-top:16px;
      }
    }
  }


  .sync-block-container {
    background-color: #FFF;
    margin-top:0px;

    .sync-block {
      display: flex;
      justify-content: center;
      align-items: center;
      padding-top: 76px;
      padding-bottom: 76px;
      flex-direction: column;

      .tick-container {
        height: 44px;
        width: 44px;
        border-radius: 50%;
        display: flex;
        justify-content: center;
        align-items: center;
        .tick-icon {
          color: #FFF;
        }
      }

      .sync-animation {
        color: $purple500;
        margin-bottom: 63px;
      }

      .button-block {
        display: flex;
        flex-direction: column;
        max-width: 100%;
        padding: 0 12px;
        margin-top: 44px;

        .button {
          width: 300px;
          max-width:100%;
        }
      }

      .last-sync-time {
        margin-top: 25px;
        font-size: 14px;
        color: #8E97A7;
        line-height: 25px;
      }

      .text-block {
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
        max-width: 100%;

        .header {
          color: $blueGrey800;
          margin-bottom: 3px;
        }
      }
    }
  }
}
</style>
